/**
 * TNWS favorites — localStorage for guests, app proxy + customer metafield when logged in.
 */
class TnwsFavoritesConfig {
  static get() {
    return window.tnwsFavoritesConfig || {};
  }

  static isLoggedIn() {
    return Boolean(this.get().loggedIn);
  }

  static proxyUrl() {
    return this.get().proxyUrl || '/apps/tnws-favorites/favorites';
  }

  static mergeStorageKey() {
    return 'tnws:favorites-merged';
  }
}

class TnwsFavorites {
  static STORAGE_KEY = 'tnws:favorites';

  static normalizeLocalItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = String(raw.id ?? raw.product_id ?? '').trim();
    const handle = String(raw.handle ?? '').trim();
    if (!id || !handle) return null;

    const variant_id =
      raw.variant_id != null && String(raw.variant_id).trim() !== '' ? String(raw.variant_id).trim() : null;
    const sku = raw.sku != null && String(raw.sku).trim() !== '' ? String(raw.sku).trim() : null;

    return { id, handle, variant_id, sku };
  }

  static getItems() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.map((item) => this.normalizeLocalItem(item)).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  static saveItems(items) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('tnws:favorites-changed', { detail: { items } }));
  }

  static has(productId) {
    const id = String(productId);
    return this.getItems().some((item) => String(item.id) === id);
  }

  static addLocal(productId, handle, variantId = null, sku = null) {
    const id = String(productId);
    const items = this.getItems().filter((item) => String(item.id) !== id);
    items.unshift({
      id,
      handle,
      variant_id: variantId ? String(variantId) : null,
      sku: sku ? String(sku) : null,
    });
    this.saveItems(items);
    return items;
  }

  static removeLocal(productId) {
    const id = String(productId);
    const items = this.getItems().filter((item) => String(item.id) !== id);
    this.saveItems(items);
    return items;
  }

  static buildItemFromButton(button) {
    const productId = button.dataset.productId;
    const handle = button.dataset.productHandle;
    if (!productId || !handle) return null;

    const variantId = button.dataset.variantId || null;
    const sku = button.dataset.sku || null;

    return {
      id: String(productId),
      handle: String(handle),
      variant_id: variantId ? String(variantId) : null,
      sku: sku ? String(sku) : null,
    };
  }

  static async proxyRequest(method, body) {
    const url = TnwsFavoritesConfig.proxyUrl();
    const options = {
      method,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    };

    if (body) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);
    const data = await response.json().catch(() => ({}));

    if (response.status === 401 && data.error === 'login_required') {
      return { loginRequired: true, items: [] };
    }

    if (!response.ok) {
      throw new Error(data.error || 'favorites_request_failed');
    }

    return data;
  }

  static async fetchRemoteItems() {
    if (!TnwsFavoritesConfig.isLoggedIn()) {
      return this.getItems();
    }

    const data = await this.proxyRequest('GET');
    if (data.loginRequired) {
      return this.getItems();
    }

    const items = Array.isArray(data.items) ? data.items : [];
    return items.map((item) => this.normalizeLocalItem(item)).filter(Boolean);
  }

  static async mergeGuestIntoAccount() {
    if (!TnwsFavoritesConfig.isLoggedIn()) return;

    const flagKey = TnwsFavoritesConfig.mergeStorageKey();
    if (sessionStorage.getItem(flagKey) === 'done') return;

    const guestItems = this.getItems();
    if (guestItems.length === 0) {
      sessionStorage.setItem(flagKey, 'done');
      return;
    }

    try {
      await this.proxyRequest('POST', { action: 'merge', items: guestItems });
      localStorage.removeItem(this.STORAGE_KEY);
      sessionStorage.setItem(flagKey, 'done');
      document.dispatchEvent(new CustomEvent('tnws:favorites-changed'));
    } catch (e) {
      console.warn('[tnws-favorites] merge failed', e);
    }
  }

  static toggleLocal(productId, handle, variantId = null, sku = null) {
    if (this.has(productId)) {
      this.removeLocal(productId);
      return false;
    }
    this.addLocal(productId, handle, variantId, sku);
    return true;
  }

  static async toggle(productId, handle, variantId = null, sku = null) {
    if (TnwsFavoritesConfig.isLoggedIn()) {
      const item = {
        id: String(productId),
        handle: String(handle),
        variant_id: variantId ? String(variantId) : null,
        sku: sku ? String(sku) : null,
      };

      try {
        const data = await this.proxyRequest('POST', { action: 'toggle', item });
        if (data.loginRequired) {
          throw new Error('login_required');
        }

        const items = Array.isArray(data.items) ? data.items : [];
        document.dispatchEvent(
          new CustomEvent('tnws:favorites-changed', {
            detail: { items: items.map((i) => this.normalizeLocalItem(i)).filter(Boolean) },
          })
        );
        return Boolean(data.active);
      } catch (e) {
        console.warn('[tnws-favorites] account sync unavailable, saving on this device', e);
      }
    }

    return this.toggleLocal(productId, handle, variantId, sku);
  }

  static async getCount() {
    const items = TnwsFavoritesConfig.isLoggedIn() ? await this.fetchRemoteItems() : this.getItems();
    return items.length;
  }

  static async updateHeaderCount() {
    const count = await this.getCount();
    document.querySelectorAll('.tnws-favorites-count').forEach((bubble) => {
      const number = bubble.querySelector('.tnws-favorites-count__number');
      if (number) number.textContent = count;
      if (count > 0) {
        bubble.removeAttribute('hidden');
      } else {
        bubble.setAttribute('hidden', '');
      }
    });
  }

  static applyButtonState(button, isActive) {
    button.classList.toggle('is-favorited', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      isActive
        ? window.tnwsFavoritesStrings?.remove || 'Remove from favorites'
        : window.tnwsFavoritesStrings?.add || 'Add to favorites'
    );
  }

  static syncButtonsFromItems(items) {
    const activeIds = new Set((items || []).map((item) => String(item.id)));
    document.querySelectorAll('[data-tnws-favorite]').forEach((button) => {
      const isActive = activeIds.has(String(button.dataset.productId));
      this.applyButtonState(button, isActive);
    });
  }

  static updateHeaderCountFromItems(items) {
    const count = Array.isArray(items) ? items.length : 0;
    document.querySelectorAll('.tnws-favorites-count').forEach((bubble) => {
      const number = bubble.querySelector('.tnws-favorites-count__number');
      if (number) number.textContent = count;
      if (count > 0) {
        bubble.removeAttribute('hidden');
      } else {
        bubble.setAttribute('hidden', '');
      }
    });
  }

  static async syncButtons() {
    let activeIds = new Set();

    if (TnwsFavoritesConfig.isLoggedIn()) {
      try {
        const items = await this.fetchRemoteItems();
        activeIds = new Set(items.map((item) => String(item.id)));
      } catch (e) {
        activeIds = new Set(this.getItems().map((item) => String(item.id)));
      }
    } else {
      activeIds = new Set(this.getItems().map((item) => String(item.id)));
    }

    document.querySelectorAll('[data-tnws-favorite]').forEach((button) => {
      const isActive = activeIds.has(String(button.dataset.productId));
      this.applyButtonState(button, isActive);
    });
  }
}

class TnwsFavoriteButtons {
  constructor() {
    this.onClick = this.onClick.bind(this);
    document.addEventListener('click', this.onClick);
    document.addEventListener('tnws:favorites-changed', (event) => {
      const items = event.detail?.items;
      if (items) {
        TnwsFavorites.updateHeaderCountFromItems(items);
        TnwsFavorites.syncButtonsFromItems(items);
        return;
      }
      TnwsFavorites.updateHeaderCount();
      TnwsFavorites.syncButtons();
    });

    this.init();
  }

  playPopAnimation(button) {
    button.classList.remove('is-popping');
    void button.offsetWidth;
    button.classList.add('is-popping');
    window.clearTimeout(button._tnwsPopTimer);
    button._tnwsPopTimer = window.setTimeout(() => {
      button.classList.remove('is-popping');
    }, 450);
  }

  async init() {
    await TnwsFavorites.mergeGuestIntoAccount();
    await TnwsFavorites.updateHeaderCount();
    await TnwsFavorites.syncButtons();
    this.bindVariantChanges();
  }

  bindVariantChanges() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant;
      if (!variant) return;

      document.querySelectorAll('.tnws-favorite-btn--pdp[data-tnws-favorite]').forEach((button) => {
        button.dataset.variantId = variant.id;
        button.dataset.sku = variant.sku || '';
      });
    });
  }

  async onClick(event) {
    const button = event.target.closest('[data-tnws-favorite]');
    if (!button || button.dataset.tnwsFavoriteBusy === 'true') return;
    event.preventDefault();
    event.stopPropagation();

    const item = TnwsFavorites.buildItemFromButton(button);
    if (!item) return;

    const wasFavorited = button.classList.contains('is-favorited');
    const optimisticActive = !wasFavorited;

    button.dataset.tnwsFavoriteBusy = 'true';
    TnwsFavorites.applyButtonState(button, optimisticActive);
    this.playPopAnimation(button);

    try {
      const isActive = await TnwsFavorites.toggle(item.id, item.handle, item.variant_id, item.sku);
      TnwsFavorites.applyButtonState(button, isActive);
      if (isActive && !wasFavorited) {
        document.dispatchEvent(new CustomEvent('tnws:favorite-added', { detail: { button, item } }));
      }
    } catch (e) {
      TnwsFavorites.applyButtonState(button, wasFavorited);
      console.warn('[tnws-favorites] toggle failed', e);
    } finally {
      delete button.dataset.tnwsFavoriteBusy;
    }
  }
}

class TnwsFavoritesPage {
  static GENDER_OPTIONS = ["Men's", "Women's", 'Unisex'];
  static QUALITY_OPTIONS = ['New stock', 'Refurbished'];
  static REFURBISHED_TAGS = new Set(['excellent refurbish', 'good refurbish', 'poor refurbish']);

  constructor(root) {
    this.root = root;
    if (!this.root) return;
    this.grid = this.root.querySelector('[data-tnws-favorites-grid]');
    this.empty = this.root.querySelector('[data-tnws-favorites-empty]');
    this.filteredEmpty = this.root.querySelector('[data-tnws-favorites-filtered-empty]');
    this.skeleton = this.root.querySelector('[data-tnws-favorites-skeleton]');
    this.filters = this.root.querySelector('[data-tnws-favorites-filters]');
    const filterScope = this.filters || this.root;
    this.filterStatus = filterScope.querySelector('[data-tnws-favorites-filter-status]');
    this.countLabel = this.root.querySelector('[data-tnws-favorites-count-label]');
    this.filterTitle = filterScope.querySelector('[data-tnws-filter-title]');
    this.filterBrandSelect = filterScope.querySelector('[data-tnws-filter-brand-select]');
    this.filterBrandText = filterScope.querySelector('[data-tnws-filter-brand-text]');
    this.filterCaseSelect = filterScope.querySelector('[data-tnws-filter-case-select]');
    this.filterCaseText = filterScope.querySelector('[data-tnws-filter-case-text]');
    this.filterPricePreset = filterScope.querySelector('[data-tnws-filter-price-preset]');
    this.filterPriceMin = filterScope.querySelector('[data-tnws-filter-price-min]');
    this.filterPriceMax = filterScope.querySelector('[data-tnws-filter-price-max]');
    this.filterColorSelect = filterScope.querySelector('[data-tnws-filter-color-select]');
    this.filterColorText = filterScope.querySelector('[data-tnws-filter-color-text]');
    this.filterGenderSelect = filterScope.querySelector('[data-tnws-filter-gender-select]');
    this.filterGenderText = filterScope.querySelector('[data-tnws-filter-gender-text]');
    this.filterFeaturesSelect = filterScope.querySelector('[data-tnws-filter-features-select]');
    this.filterFeaturesText = filterScope.querySelector('[data-tnws-filter-features-text]');
    this.filterQualitySelect = filterScope.querySelector('[data-tnws-filter-quality-select]');
    this.filterQualityText = filterScope.querySelector('[data-tnws-filter-quality-text]');
    this.filterDrawer = filterScope.querySelector('[data-tnws-filter-drawer]');
    this.openPill = null;
    this.allEntries = [];
    this.filterDebounce = null;
    this.pricePresets = {
      '0-2500': { min: 0, max: 2500 },
      '2500-5000': { min: 2500, max: 5000 },
      '5000-10000': { min: 5000, max: 10000 },
      '10000-25000': { min: 10000, max: 25000 },
      '25000+': { min: 25000, max: null },
    };
    this.portalFilterDrawer();
    this.bindFilters();
    this.refreshComboboxes();
    document.addEventListener('tnws:favorites-changed', () => this.render());
    this.render();
  }

  portalFilterDrawer() {
    if (!this.filterDrawer || this.filterDrawer.dataset.tnwsDrawerPortaled === 'true') return;
    document.body.appendChild(this.filterDrawer);
    this.filterDrawer.dataset.tnwsDrawerPortaled = 'true';
  }

  queryFilterElements(selector) {
    const seen = new Set();
    const elements = [];
    const add = (root) => {
      if (!root) return;
      root.querySelectorAll(selector).forEach((element) => {
        if (seen.has(element)) return;
        seen.add(element);
        elements.push(element);
      });
    };
    add(this.filters || this.root);
    add(this.filterDrawer);
    return elements;
  }

  refreshComboboxes() {
    if (!window.TnwsFilterCombobox) return;
    if (this.filters) TnwsFilterCombobox.initAll(this.filters);
    if (this.filterDrawer) TnwsFilterCombobox.initAll(this.filterDrawer);
  }

  revealComboboxLists(scope = null) {
    if (!window.TnwsFilterCombobox) return;
    const scopes = scope ? [scope] : [this.filters || this.root, this.filterDrawer].filter(Boolean);
    scopes.forEach((root) => {
      root.querySelectorAll('[data-tnws-filter-combobox]').forEach((element) => {
        const instance = TnwsFilterCombobox.instances.get(element);
        if (instance) instance.showList();
      });
    });
  }

  bindSyncedInputs(attr, handler) {
    this.queryFilterElements(`[${attr}]`).forEach((element) => {
      const onInput = () => {
        const { value } = element;
        this.queryFilterElements(`[${attr}]`).forEach((other) => {
          if (other !== element) other.value = value;
        });
        handler();
      };
      element.addEventListener('input', onInput);
      element.addEventListener('search', onInput);
    });
  }

  bindSyncedSelects(attr, handler) {
    this.queryFilterElements(`[${attr}]`).forEach((element) => {
      element.addEventListener('change', () => {
        const { value } = element;
        this.queryFilterElements(`[${attr}]`).forEach((other) => {
          if (other !== element) other.value = value;
        });
        handler();
      });
    });
  }

  bindSyncedPriceFields(handler) {
    this.queryFilterElements('[data-tnws-filter-price-preset]').forEach((element) => {
      element.addEventListener('change', () => {
        const { value } = element;
        this.queryFilterElements('[data-tnws-filter-price-preset]').forEach((other) => {
          if (other !== element) other.value = value;
        });
        if (value) {
          this.queryFilterElements('[data-tnws-filter-price-min]').forEach((input) => {
            input.value = '';
          });
          this.queryFilterElements('[data-tnws-filter-price-max]').forEach((input) => {
            input.value = '';
          });
        }
        handler();
      });
    });

    this.queryFilterElements('[data-tnws-filter-price-min]').forEach((element) => {
      const onInput = () => {
        const { value } = element;
        this.queryFilterElements('[data-tnws-filter-price-min]').forEach((other) => {
          if (other !== element) other.value = value;
        });
        handler();
      };
      element.addEventListener('input', onInput);
      element.addEventListener('search', onInput);
      element.addEventListener('change', () => {
        this.queryFilterElements('[data-tnws-filter-price-preset]').forEach((input) => {
          input.value = '';
        });
        handler();
      });
    });

    this.queryFilterElements('[data-tnws-filter-price-max]').forEach((element) => {
      const onInput = () => {
        const { value } = element;
        this.queryFilterElements('[data-tnws-filter-price-max]').forEach((other) => {
          if (other !== element) other.value = value;
        });
        handler();
      };
      element.addEventListener('input', onInput);
      element.addEventListener('search', onInput);
      element.addEventListener('change', () => {
        this.queryFilterElements('[data-tnws-filter-price-preset]').forEach((input) => {
          input.value = '';
        });
        handler();
      });
    });
  }

  bindFilters() {
    const filterScope = this.filters || this.root;
    const onFilterChange = () => {
      window.clearTimeout(this.filterDebounce);
      this.filterDebounce = window.setTimeout(() => {
        this.populateFilterOptions();
        this.updatePillStates();
        this.applyFilters();
      }, 150);
    };

    this.bindSyncedInputs('data-tnws-filter-title', onFilterChange);
    this.bindSyncedPriceFields(onFilterChange);

    [
      'data-tnws-filter-brand-select',
      'data-tnws-filter-case-select',
      'data-tnws-filter-color-select',
      'data-tnws-filter-gender-select',
      'data-tnws-filter-features-select',
      'data-tnws-filter-quality-select',
    ].forEach((attr) => this.bindSyncedSelects(attr, onFilterChange));

    filterScope.querySelectorAll('[data-tnws-filter-pill]').forEach((pill) => {
      pill.addEventListener('click', (event) => {
        event.stopPropagation();
        const key = pill.dataset.tnwsFilterPill;
        this.togglePillDropdown(key, pill);
      });
    });

    filterScope.querySelectorAll('[data-tnws-filter-pill-clear]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        this.closePillDropdowns();
        this.clearSingleFilter(button.dataset.tnwsFilterPillClear);
      });
    });

    this.bindPillDropdownHoverClose(filterScope);

    filterScope.addEventListener('change', (event) => {
      if (!this.isMobileFilterUi()) return;
      if (!event.target.closest('[data-tnws-filter-dropdown]')) return;
      window.setTimeout(() => this.closePillDropdowns(), 0);
    });

    filterScope.querySelectorAll('[data-tnws-filter-more-open], [data-tnws-filter-mobile-open]').forEach((button) => {
      button.addEventListener('click', () => this.openMoreDrawer());
    });

    const mobileChips = filterScope.querySelector('[data-tnws-filter-mobile-chips]');
    if (mobileChips && mobileChips.dataset.tnwsMobileChipsBound !== 'true') {
      mobileChips.dataset.tnwsMobileChipsBound = 'true';
      mobileChips.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-tnws-filter-chip-clear]');
        if (!chip) return;
        this.clearSingleFilter(chip.dataset.tnwsFilterChipClear);
      });
    }

    this.queryFilterElements('[data-tnws-filter-mobile-clear]').forEach((button) => {
      button.addEventListener('click', () => this.clearFilters());
    });

    const drawerClose = (this.filterDrawer || filterScope).querySelectorAll('[data-tnws-filter-drawer-close]');
    drawerClose.forEach((button) => {
      button.addEventListener('click', () => this.closeMoreDrawer());
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (this.filterDrawer?.contains(target)) return;
      if (target.closest('[data-tnws-filter-dropdown]')) return;
      if (target.closest('[data-tnws-filter-pill]')) return;
      if (target.closest('[data-tnws-filter-pill-clear]')) return;
      this.closePillDropdowns();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeMoreDrawer();
        this.closePillDropdowns();
      }
    });

    filterScope.querySelectorAll('[data-tnws-filter-clear]').forEach((button) => {
      button.addEventListener('click', () => this.clearFilters());
    });
    this.bindFilterAccordionLists();
    this.root.querySelectorAll('[data-tnws-filter-clear-inline]').forEach((button) => {
      button.addEventListener('click', () => this.clearFilters());
    });
  }

  isMobileFilterUi() {
    if (!this._mobileFilterMql) {
      this._mobileFilterMql = window.matchMedia('(max-width: 749px)');
    }
    return this._mobileFilterMql.matches;
  }

  portalPillDropdown(dropdown) {
    if (!dropdown || dropdown.dataset.tnwsDropdownPortaled === 'true') return;

    dropdown._tnwsDropdownParent = dropdown.parentElement;
    dropdown._tnwsDropdownNextSibling = dropdown.nextSibling;
    document.body.appendChild(dropdown);
    dropdown.dataset.tnwsDropdownPortaled = 'true';
  }

  restorePillDropdown(dropdown) {
    if (!dropdown || dropdown.dataset.tnwsDropdownPortaled !== 'true') return;

    const parent = dropdown._tnwsDropdownParent;
    if (parent) {
      const next = dropdown._tnwsDropdownNextSibling;
      if (next && next.parentElement === parent) {
        parent.insertBefore(dropdown, next);
      } else {
        parent.appendChild(dropdown);
      }
    }

    dropdown.dataset.tnwsDropdownPortaled = '';
    delete dropdown._tnwsDropdownParent;
    delete dropdown._tnwsDropdownNextSibling;
  }

  positionDesktopPillDropdown(dropdown, pill) {
    if (!dropdown || !pill) return;

    const rect = pill.getBoundingClientRect();
    const gutter = 8;
    const minWidth = Math.max(rect.width, 272);
    let left = rect.left;

    if (left + minWidth > window.innerWidth - gutter) {
      left = Math.max(gutter, window.innerWidth - minWidth - gutter);
    }

    dropdown.style.position = 'fixed';
    dropdown.style.top = `${rect.bottom + 6}px`;
    dropdown.style.left = `${left}px`;
    dropdown.style.right = 'auto';
    dropdown.style.bottom = 'auto';
    dropdown.style.width = 'max-content';
    dropdown.style.minWidth = `${minWidth}px`;
    dropdown.style.maxHeight = 'min(70vh, 24rem)';
    dropdown.style.overflowY = 'auto';
    dropdown.style.zIndex = '120';
  }

  bindDesktopPillDropdownPosition() {
    if (this._desktopPillDropdownPosition) return;

    this._desktopPillDropdownPosition = () => {
      const scope = this.filters || this.root;
      const openPill = scope?.querySelector('[data-tnws-filter-pill][aria-expanded="true"]');
      const openDropdown = document.body.querySelector(
        '[data-tnws-filter-dropdown][data-tnws-dropdown-portaled="true"]:not([hidden])'
      );
      if (openPill && openDropdown) this.positionDesktopPillDropdown(openDropdown, openPill);
    };

    window.addEventListener('scroll', this._desktopPillDropdownPosition, true);
    window.addEventListener('resize', this._desktopPillDropdownPosition);
  }

  unbindDesktopPillDropdownPosition() {
    if (!this._desktopPillDropdownPosition) return;
    window.removeEventListener('scroll', this._desktopPillDropdownPosition, true);
    window.removeEventListener('resize', this._desktopPillDropdownPosition);
  }

  openPillSheet(dropdown, pill) {
    if (!dropdown) {
      const scope = this.filters || this.root;
      dropdown = scope?.querySelector('[data-tnws-filter-dropdown]:not([hidden])');
    }
    if (!dropdown) return;

    this.portalPillDropdown(dropdown);

    if (this.isMobileFilterUi()) {
      document.body.classList.add('tnws-fav-filters-pill-sheet-open');
      return;
    }

    dropdown.dataset.tnwsDropdownDesktop = 'true';
    if (pill) this.positionDesktopPillDropdown(dropdown, pill);
    this.bindDesktopPillDropdownPosition();
    this.bindPortaledDropdownHover(dropdown);
  }

  bindPortaledDropdownHover(dropdown) {
    if (!dropdown || dropdown.dataset.tnwsPortaledHoverBound === 'true') return;
    dropdown.dataset.tnwsPortaledHoverBound = 'true';

    dropdown.addEventListener('mouseenter', () => {
      const scope = this.filters || this.root;
      const group = scope?.querySelector(`[data-tnws-filter-group="${dropdown.dataset.tnwsFilterDropdown}"]`);
      if (group) {
        window.clearTimeout(group._tnwsPillCloseTimer);
        group._tnwsPillCloseTimer = null;
      }
      window.clearTimeout(dropdown._tnwsPillCloseTimer);
      dropdown._tnwsPillCloseTimer = null;
    });

    dropdown.addEventListener('mouseleave', () => {
      if (!this._pillHoverMql?.matches) return;
      const scope = this.filters || this.root;
      const pill = scope?.querySelector(
        `[data-tnws-filter-pill="${dropdown.dataset.tnwsFilterDropdown}"][aria-expanded="true"]`
      );
      if (!pill) return;

      window.clearTimeout(dropdown._tnwsPillCloseTimer);
      dropdown._tnwsPillCloseTimer = window.setTimeout(() => {
        if (dropdown.matches(':hover') || pill.matches(':hover')) return;
        if (pill.getAttribute('aria-expanded') !== 'true') return;
        this.closePillDropdowns();
      }, 220);
    });
  }

  closePillSheet() {
    document.body.classList.remove('tnws-fav-filters-pill-sheet-open');
    document.getElementById('tnws-filters-pill-sheet-backdrop')?.remove();
    this.unbindDesktopPillDropdownPosition();
    document.body.querySelectorAll('[data-tnws-filter-dropdown][data-tnws-dropdown-portaled="true"]').forEach((dropdown) => {
      dropdown.style.cssText = '';
      dropdown.dataset.tnwsDropdownDesktop = '';
      this.restorePillDropdown(dropdown);
    });
  }

  syncPillSheetHeader(dropdown, pill) {
    if (!this.isMobileFilterUi()) return;

    let header = dropdown.querySelector('[data-tnws-filter-sheet-header]');
    if (!header) {
      header = document.createElement('div');
      header.className = 'tnws-fav-filters__dropdown-sheet-header';
      header.dataset.tnwsFilterSheetHeader = '';

      const title = document.createElement('span');
      title.className = 'tnws-fav-filters__dropdown-sheet-title';
      title.dataset.tnwsFilterSheetTitle = '';

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'tnws-fav-filters__dropdown-sheet-close';
      closeBtn.dataset.tnwsFilterSheetClose = '';
      closeBtn.setAttribute('aria-label', 'Close filter');
      closeBtn.textContent = '×';
      closeBtn.addEventListener('click', () => this.closePillDropdowns());

      header.append(title, closeBtn);
      dropdown.prepend(header);
    }

    const label = pill.querySelector('[data-tnws-filter-pill-label]');
    header.querySelector('[data-tnws-filter-sheet-title]').textContent = label?.textContent?.trim() || 'Filter';
  }

  togglePillDropdown(key, pill) {
    const dropdown = (this.filters || this.root).querySelector(`[data-tnws-filter-dropdown="${key}"]`);
    if (!dropdown) return;

    const isOpen = pill.getAttribute('aria-expanded') === 'true';
    this.closePillDropdowns();

    if (!isOpen) {
      dropdown.hidden = false;
      pill.setAttribute('aria-expanded', 'true');
      pill.classList.add('is-open');
      this.openPill = key;
      this.syncPillSheetHeader(dropdown, pill);
      this.openPillSheet(dropdown, pill);
      this.populateFilterOptions();
      this.revealComboboxLists(dropdown);
      const input = dropdown.querySelector('[data-tnws-filter-combobox-input]');
      if (input && !this._pillHoverMql?.matches) {
        window.setTimeout(() => input.focus(), this.isMobileFilterUi() ? 180 : 0);
      }
    }
  }

  bindPillDropdownHoverClose(scope = this.filters || this.root) {
    if (!scope) return;

    if (!this._pillHoverMql) {
      this._pillHoverMql = window.matchMedia('(hover: hover) and (pointer: fine)');
    }

    scope.querySelectorAll('[data-tnws-filter-group]').forEach((group) => {
      if (group.dataset.tnwsPillHoverBound === 'true') return;
      group.dataset.tnwsPillHoverBound = 'true';

      const cancelClose = () => {
        window.clearTimeout(group._tnwsPillCloseTimer);
        group._tnwsPillCloseTimer = null;
      };

      group.addEventListener('mouseenter', cancelClose);
      group.addEventListener('mouseleave', () => {
        if (!this._pillHoverMql.matches) return;

        const pill = group.querySelector('[data-tnws-filter-pill]');
        if (!pill || pill.getAttribute('aria-expanded') !== 'true') return;

        cancelClose();
        group._tnwsPillCloseTimer = window.setTimeout(() => {
          if (group.matches(':hover')) return;
          const portaled = document.body.querySelector(
            `[data-tnws-filter-dropdown="${group.dataset.tnwsFilterGroup}"][data-tnws-dropdown-portaled="true"]:not([hidden])`
          );
          if (portaled?.matches(':hover')) return;
          if (pill.getAttribute('aria-expanded') !== 'true') return;
          this.closePillDropdowns();
        }, 220);
      });
    });
  }

  bindFilterAccordionLists() {
    this.queryFilterElements('.tnws-fav-filters__accordion').forEach((accordion) => {
      if (accordion.dataset.tnwsAccordionListBound === 'true') return;
      accordion.dataset.tnwsAccordionListBound = 'true';
      accordion.addEventListener('toggle', () => {
        if (!accordion.open) return;
        const combobox = accordion.querySelector('[data-tnws-filter-combobox]');
        const instance = combobox && window.TnwsFilterCombobox?.instances.get(combobox);
        if (instance) instance.showList();
      });
    });
  }

  closePillDropdowns() {
    this.closePillSheet();
    const scope = this.filters || this.root;
    scope.querySelectorAll('[data-tnws-filter-pill]').forEach((pill) => {
      pill.setAttribute('aria-expanded', 'false');
      pill.classList.remove('is-open');
    });
    scope.querySelectorAll('[data-tnws-filter-dropdown]').forEach((dropdown) => {
      dropdown.hidden = true;
    });
    this.openPill = null;
  }

  openMoreDrawer() {
    this.closePillDropdowns();
    if (!this.filterDrawer) return;
    this.filterDrawer.hidden = false;
    document.body.classList.add('tnws-fav-filters-drawer-open');
    this.populateFilterOptions();
    this.bindFilterAccordionLists();
    this.revealComboboxLists(this.filterDrawer);
    this.updateMobileFilterUi(this.lastVisibleCount);
  }

  closeMoreDrawer() {
    if (!this.filterDrawer) return;
    this.filterDrawer.hidden = true;
    document.body.classList.remove('tnws-fav-filters-drawer-open');
    this.resetFilterBarScroll();
  }

  resetFilterBarScroll() {
    const bar = (this.filters || this.root)?.querySelector('.tnws-fav-filters__bar');
    if (bar) bar.scrollLeft = 0;
  }

  getFilterState() {
    return {
      title: (this.filterTitle?.value || '').trim().toLowerCase(),
      brandSelect: (this.filterBrandSelect?.value || '').trim(),
      caseSelect: (this.filterCaseSelect?.value || '').trim(),
      pricePreset: this.filterPricePreset?.value || '',
      priceMin: (this.filterPriceMin?.value || '').trim(),
      priceMax: (this.filterPriceMax?.value || '').trim(),
      colorSelect: (this.filterColorSelect?.value || '').trim(),
      genderSelect: (this.filterGenderSelect?.value || '').trim(),
      featuresSelect: (this.filterFeaturesSelect?.value || '').trim(),
      qualitySelect: (this.filterQualitySelect?.value || '').trim(),
    };
  }

  hasActiveFilters(filters = this.getFilterState()) {
    const bounds = this.getPriceBounds(filters);
    return Boolean(
      filters.title ||
        filters.brandSelect ||
        filters.caseSelect ||
        filters.colorSelect ||
        filters.genderSelect ||
        filters.featuresSelect ||
        filters.qualitySelect ||
        filters.pricePreset ||
        filters.priceMin ||
        filters.priceMax ||
        bounds.min != null ||
        bounds.max != null
    );
  }

  clearFilters() {
    if (this.filterTitle) this.filterTitle.value = '';
    if (this.filterBrandSelect) this.filterBrandSelect.value = '';
    if (this.filterCaseSelect) this.filterCaseSelect.value = '';
    if (this.filterPricePreset) this.filterPricePreset.value = '';
    if (this.filterPriceMin) this.filterPriceMin.value = '';
    if (this.filterPriceMax) this.filterPriceMax.value = '';
    if (this.filterColorSelect) this.filterColorSelect.value = '';
    if (this.filterGenderSelect) this.filterGenderSelect.value = '';
    if (this.filterFeaturesSelect) this.filterFeaturesSelect.value = '';
    if (this.filterQualitySelect) this.filterQualitySelect.value = '';
    this.queryFilterElements('[data-tnws-filter-combobox-input]').forEach((el) => {
      el.value = '';
    });
    this.refreshComboboxes();
    this.closeMoreDrawer();
    this.closePillDropdowns();
    this.updatePillStates();
    this.applyFilters();
  }

  fillSelect(select, allLabel, values) {
    if (!select) return;
    const current = select.value;
    select.replaceChildren();
    const emptyOption = document.createElement('option');
    emptyOption.value = '';
    emptyOption.textContent = allLabel;
    select.appendChild(emptyOption);
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });
    if (current && values.includes(current)) select.value = current;
  }

  fillAllSelects(attr, allLabel, values) {
    this.queryFilterElements(`[${attr}]`).forEach((select) => {
      this.fillSelect(select, allLabel, values);
    });
  }

  populateFilterOptions() {
    const strings = window.tnwsFavoritesStrings || {};

    this.fillAllSelects(
      'data-tnws-filter-brand-select',
      strings.filterAllBrands || 'All brands',
      this.getAvailableBrandOptions()
    );
    this.fillAllSelects(
      'data-tnws-filter-case-select',
      strings.filterAllCaseSizes || 'All case sizes',
      this.getAvailableCaseSizeOptions()
    );
    this.fillAllSelects(
      'data-tnws-filter-color-select',
      strings.filterAllColors || 'All colors',
      this.getAvailableColorOptions()
    );
    this.fillAllSelects(
      'data-tnws-filter-gender-select',
      strings.filterAllGenders || 'All',
      this.getAvailableGenderOptions()
    );
    this.fillAllSelects(
      'data-tnws-filter-features-select',
      strings.filterAllFeatures || 'All features',
      this.getAvailableFeaturesOptions()
    );
    this.populateQualitySelect();
    this.updatePillStates();
    this.refreshComboboxes();
    this.revealOpenPillDropdown();
    this.resetFilterBarScroll();
  }

  revealOpenPillDropdown() {
    const scope = this.filters || this.root;
    const openDropdown = scope?.querySelector('[data-tnws-filter-dropdown]:not([hidden])');
    if (openDropdown) this.revealComboboxLists(openDropdown);
  }

  populateQualitySelect() {
    const selects = this.queryFilterElements('[data-tnws-filter-quality-select]');
    if (!selects.length) return;
    const strings = window.tnwsFavoritesStrings || {};
    const available = this.getAvailableQualityOptions();
    const options = [
      { value: 'New stock', label: strings.filterQualityNewStock || 'New stock' },
      { value: 'Refurbished', label: strings.filterQualityRefurbished || 'Refurbished' },
    ].filter((option) => available.includes(option.value));
    selects.forEach((select) => {
      const current = select.value;
      select.replaceChildren();
      const emptyOption = document.createElement('option');
      emptyOption.value = '';
      emptyOption.textContent = strings.filterAllQualities || 'All';
      select.appendChild(emptyOption);
      options.forEach((option) => {
        const el = document.createElement('option');
        el.value = option.value;
        el.textContent = option.label;
        select.appendChild(el);
      });
      if (current && options.some((option) => option.value === current)) {
        select.value = current;
      }
    });
  }

  formatPillLabel(base, hint) {
    if (!hint) return base;
    return `${base}: ${hint}`;
  }

  updatePillStates() {
    const strings = this.strings();
    const state = this.getFilterState();
    const priceHint = this.getPriceChipLabel(state);
    const pillMap = {
      brand: {
        base: strings.filterPillBrands || 'Brands',
        active: Boolean(state.brandSelect),
        hint: state.brandSelect,
      },
      case: {
        base: strings.filterPillCaseSize || 'Case Size',
        active: Boolean(state.caseSelect),
        hint: state.caseSelect,
      },
      price: {
        base: strings.filterPillPrice || 'Price',
        active: Boolean(priceHint),
        hint: priceHint,
      },
      quality: {
        base: strings.filterPillQuality || 'Quality',
        active: Boolean(state.qualitySelect),
        hint: state.qualitySelect,
      },
      color: {
        base: strings.filterColorLabel || 'Color',
        active: Boolean(state.colorSelect),
        hint: state.colorSelect,
      },
      gender: {
        base: strings.filterGenderLabel || 'Gender',
        active: Boolean(state.genderSelect),
        hint: state.genderSelect,
      },
      features: {
        base: strings.filterFeaturesLabel || 'Features',
        active: Boolean(state.featuresSelect),
        hint: state.featuresSelect,
      },
    };

    const scope = this.filters || this.root;
    Object.entries(pillMap).forEach(([key, meta]) => {
      const label = scope.querySelector(`[data-tnws-filter-pill-label="${key}"]`);
      const pill = scope.querySelector(`[data-tnws-filter-pill="${key}"]`);
      if (!label || !pill) return;
      label.textContent = meta.active ? this.formatPillLabel(meta.base, meta.hint) : meta.base;
      pill.classList.toggle('is-active', meta.active);
      const clearButton = scope.querySelector(`[data-tnws-filter-pill-clear="${key}"]`);
      if (clearButton) clearButton.hidden = !meta.active;
      const caret = pill.querySelector('.tnws-fav-filters__pill-caret');
      if (caret) caret.hidden = meta.active;
    });

    const moreButton = scope.querySelector('[data-tnws-filter-more-open]');
    const moreLabel = scope.querySelector('[data-tnws-filter-pill-label="more"]');
    const moreParts = [state.colorSelect, state.genderSelect, state.featuresSelect].filter(Boolean);
    const moreActive = moreParts.length > 0;
    const moreClear = scope.querySelector('[data-tnws-filter-pill-clear="more"]');
    if (moreButton) {
      moreButton.classList.toggle('is-active', moreActive);
      if (moreLabel) {
        const base = strings.filterPillMore || strings.filterMobileOpen || 'More Filters';
        moreLabel.textContent = moreActive ? this.formatPillLabel(base, moreParts.join(', ')) : base;
      }
    }
    if (moreClear) moreClear.hidden = !moreActive;

    this.updateMobileFilterUi(this.lastVisibleCount);
  }

  strings() {
    return window.tnwsFavoritesStrings || {};
  }

  getActiveFilterCount(state = this.getFilterState()) {
    let count = 0;
    if (state.title) count += 1;
    if (state.brandSelect) count += 1;
    if (state.caseSelect) count += 1;
    if (state.qualitySelect) count += 1;
    if (state.colorSelect) count += 1;
    if (state.genderSelect) count += 1;
    if (state.featuresSelect) count += 1;
    if (state.pricePreset || state.priceMin || state.priceMax) count += 1;
    return count;
  }

  getPriceChipLabel(state = this.getFilterState()) {
    const strings = this.strings();
    if (state.pricePreset) {
      const preset = this.queryFilterElements('[data-tnws-filter-price-preset]')[0];
      const match = preset ? [...preset.options].find((option) => option.value === state.pricePreset) : null;
      return match?.textContent?.trim() || state.pricePreset;
    }
    if (state.priceMin && state.priceMax) return `$${state.priceMin} – $${state.priceMax}`;
    if (state.priceMin) return `${strings.filterPriceMin || 'Min $'}${state.priceMin}`;
    if (state.priceMax) return `${strings.filterPriceMax || 'Max $'}${state.priceMax}`;
    return '';
  }

  buildMobileFilterChips(state = this.getFilterState()) {
    const strings = this.strings();
    const chips = [];
    const addChip = (key, label, value) => {
      if (!value) return;
      chips.push({ key, label: `${label}: ${value}` });
    };

    if (state.title) addChip('title', strings.filterSearchTitle || 'Search', state.title);
    addChip('brand', strings.filterPillBrands || 'Brands', state.brandSelect);
    addChip('case', strings.filterPillCaseSize || 'Case Size', state.caseSelect);
    addChip('quality', strings.filterPillQuality || 'Quality', state.qualitySelect);
    addChip('color', strings.filterColorLabel || 'Color', state.colorSelect);
    addChip('gender', strings.filterGenderLabel || 'Gender', state.genderSelect);
    addChip('features', strings.filterFeaturesLabel || 'Features', state.featuresSelect);
    const priceLabel = this.getPriceChipLabel(state);
    if (priceLabel) addChip('price', strings.filterPillPrice || 'Price', priceLabel);

    return chips;
  }

  updateMobileFilterUi(visibleCount = this.lastVisibleCount) {
    const scope = this.filters || this.root;
    if (!scope) return;

    const mobileOpen = scope.querySelector('[data-tnws-filter-mobile-open]');
    const badge = scope.querySelector('[data-tnws-filter-mobile-badge]');
    const mobileClears = this.queryFilterElements('[data-tnws-filter-mobile-clear]');
    const chipsRoot = scope.querySelector('[data-tnws-filter-mobile-chips]');
    const applyLabel = this.filterDrawer?.querySelector('[data-tnws-filter-drawer-apply-label]');
    const searchInput = scope.querySelector('[data-tnws-filter-title]');
    const state = this.getFilterState();
    const activeCount = this.getActiveFilterCount(state);
    const chips = this.buildMobileFilterChips(state);
    const strings = this.strings();
    const accordionActive = {
      brand: Boolean(state.brandSelect),
      case: Boolean(state.caseSelect),
      quality: Boolean(state.qualitySelect),
      price: Boolean(state.pricePreset || state.priceMin || state.priceMax),
      color: Boolean(state.colorSelect),
      gender: Boolean(state.genderSelect),
      features: Boolean(state.featuresSelect),
    };

    if (mobileOpen) mobileOpen.classList.toggle('is-active', activeCount > 0);
    if (badge) {
      badge.hidden = activeCount === 0;
      badge.textContent = String(activeCount);
    }
    mobileClears.forEach((button) => {
      button.hidden = activeCount === 0;
    });
    scope.querySelectorAll('[data-tnws-filter-clear]').forEach((button) => {
      button.hidden = activeCount === 0;
    });
    if (searchInput) searchInput.classList.toggle('is-active', Boolean(state.title));

    this.filterDrawer?.querySelectorAll('[data-tnws-filter-accordion]').forEach((accordion) => {
      const key = accordion.dataset.tnwsFilterAccordion;
      accordion.classList.toggle('is-active', Boolean(accordionActive[key]));
    });

    if (chipsRoot) {
      chipsRoot.replaceChildren();
      chips.forEach((chip) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tnws-fav-filters__mobile-chip';
        button.dataset.tnwsFilterChipClear = chip.key;
        const labelSpan = document.createElement('span');
        labelSpan.textContent = chip.label;
        const removeSpan = document.createElement('span');
        removeSpan.className = 'tnws-fav-filters__mobile-chip-remove';
        removeSpan.setAttribute('aria-hidden', 'true');
        removeSpan.textContent = '×';
        button.append(labelSpan, removeSpan);
        button.setAttribute('aria-label', `Remove ${chip.label}`);
        chipsRoot.appendChild(button);
      });
      chipsRoot.hidden = chips.length === 0;
    }

    if (applyLabel && this.isMobileFilterUi()) {
      const count = visibleCount == null ? 0 : visibleCount;
      applyLabel.textContent = strings.filterMobileShowResults
        ? strings.filterMobileShowResults.replace('__COUNT__', String(count))
        : `Show ${count} watches`;
    }
  }

  clearSingleFilter(key) {
    const clearValue = (attr) => {
      this.queryFilterElements(`[${attr}]`).forEach((element) => {
        element.value = '';
      });
    };

    switch (key) {
      case 'title':
        clearValue('data-tnws-filter-title');
        break;
      case 'brand':
        clearValue('data-tnws-filter-brand-select');
        clearValue('data-tnws-filter-brand-text');
        break;
      case 'case':
        clearValue('data-tnws-filter-case-select');
        clearValue('data-tnws-filter-case-text');
        break;
      case 'quality':
        clearValue('data-tnws-filter-quality-select');
        clearValue('data-tnws-filter-quality-text');
        break;
      case 'color':
        clearValue('data-tnws-filter-color-select');
        clearValue('data-tnws-filter-color-text');
        break;
      case 'gender':
        clearValue('data-tnws-filter-gender-select');
        clearValue('data-tnws-filter-gender-text');
        break;
      case 'features':
        clearValue('data-tnws-filter-features-select');
        clearValue('data-tnws-filter-features-text');
        break;
      case 'price':
        clearValue('data-tnws-filter-price-preset');
        clearValue('data-tnws-filter-price-min');
        clearValue('data-tnws-filter-price-max');
        break;
      case 'more':
        clearValue('data-tnws-filter-color-select');
        clearValue('data-tnws-filter-color-text');
        clearValue('data-tnws-filter-gender-select');
        clearValue('data-tnws-filter-gender-text');
        clearValue('data-tnws-filter-features-select');
        clearValue('data-tnws-filter-features-text');
        break;
      default:
        break;
    }

    this.queryFilterElements('[data-tnws-filter-combobox-input]').forEach((element) => {
      const select = element.closest('[data-tnws-filter-combobox]')?.querySelector('select');
      if (select && !select.value) element.value = '';
    });

    this.refreshComboboxes();
    this.populateFilterOptions();
    this.updatePillStates();
    this.applyFilters();
  }

  getTagGroupConfig() {
    return window.TNWS_TAG_GROUPS || { groups: {}, filters: {} };
  }

  getFilterGroupKeys(filterKey) {
    const mapping = this.getTagGroupConfig().filters?.[filterKey];
    if (!mapping) return [];
    if (Array.isArray(mapping)) return mapping.filter(Boolean);
    return mapping ? [mapping] : [];
  }

  getAllowedTagsForFilter(filterKey) {
    const { groups = {} } = this.getTagGroupConfig();
    const seen = new Map();

    this.getFilterGroupKeys(filterKey).forEach((groupKey) => {
      (groups[groupKey] || []).forEach((tag) => {
        const normalized = String(tag).trim();
        if (!normalized) return;
        const lookup = normalized.toLowerCase();
        if (!seen.has(lookup)) seen.set(lookup, normalized);
      });
    });

    return [...seen.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
    );
  }

  getEntryTagsForFilter(product, filterKey) {
    const allowed = this.getAllowedTagsForFilter(filterKey);
    if (!allowed.length) return [];

    const allowedLookup = new Map(allowed.map((tag) => [tag.toLowerCase(), tag]));
    const matches = new Map();

    this.normalizeTags(product).forEach((tag) => {
      const canonical = allowedLookup.get(tag.toLowerCase());
      if (canonical) matches.set(canonical.toLowerCase(), canonical);
    });

    return [...matches.values()];
  }

  getRelaxedFilterState(excludeKey) {
    const state = this.getFilterState();
    if (!excludeKey) return state;

    const relaxed = { ...state };
    switch (excludeKey) {
      case 'brand':
        relaxed.brandSelect = '';
        break;
      case 'case':
        relaxed.caseSelect = '';
        break;
      case 'color':
        relaxed.colorSelect = '';
        break;
      case 'gender':
        relaxed.genderSelect = '';
        break;
      case 'features':
        relaxed.featuresSelect = '';
        break;
      case 'quality':
        relaxed.qualitySelect = '';
        break;
      case 'price':
        relaxed.pricePreset = '';
        relaxed.priceMin = '';
        relaxed.priceMax = '';
        break;
      default:
        break;
    }
    return relaxed;
  }

  getFilteredPool(excludeKey = null) {
    const filters = excludeKey ? this.getRelaxedFilterState(excludeKey) : this.getFilterState();
    return this.allEntries.filter((entry) => this.matchesFilters(entry, filters));
  }

  getEntryCaseSizes(product) {
    const allowed = this.getAllowedTagsForFilter('case');
    if (!allowed.length) return [];

    const allowedLookup = new Map(allowed.map((tag) => [tag.toLowerCase(), tag]));
    const found = new Map();

    const addSize = (raw) => {
      const lower = String(raw || '').trim().toLowerCase();
      if (!lower) return;
      if (allowedLookup.has(lower)) {
        found.set(lower, allowedLookup.get(lower));
        return;
      }
      const match = lower.match(/(\d{1,2})\s*mm\b/);
      if (!match) return;
      const key = `${match[1]}mm`;
      if (allowedLookup.has(key)) found.set(key, allowedLookup.get(key));
    };

    this.normalizeTags(product).forEach(addSize);
    addSize(String(product?.title || ''));
    return [...found.values()];
  }

  collectCaseSizesFromEntries(entries = []) {
    const allowed = this.getAllowedTagsForFilter('case');
    const allowedLookup = new Set(allowed.map((tag) => tag.toLowerCase()));
    const found = new Map();

    entries.forEach((entry) => {
      this.getEntryCaseSizes(entry.product).forEach((size) => {
        const lookup = size.toLowerCase();
        if (!allowedLookup.has(lookup)) return;
        found.set(lookup, size);
      });
    });

    return [...found.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
    );
  }

  getAvailableCaseSizeOptions() {
    const allowed = this.getAllowedTagsForFilter('case');
    if (!allowed.length) return [];

    const fromFiltered = this.collectCaseSizesFromEntries(this.getFilteredPool('case'));
    if (fromFiltered.length) return fromFiltered;

    const fromPool = this.collectCaseSizesFromEntries(this.allEntries);
    if (fromPool.length) return fromPool;

    return allowed;
  }

  getBrandFromTitle(product) {
    const allowed = this.getAllowedTagsForFilter('brand');
    if (!allowed.length) return null;

    const title = String(product?.title || '').trim().toLowerCase();
    if (!title) return null;

    const sorted = [...allowed].sort((a, b) => b.length - a.length);
    for (const brand of sorted) {
      const brandLower = brand.toLowerCase();
      if (title === brandLower || title.startsWith(`${brandLower} `)) return brand;
    }

    return null;
  }

  getEntryValuesForFilter(product, filterKey) {
    if (filterKey === 'case') return this.getEntryCaseSizes(product);

    const tagValues = this.getEntryTagsForFilter(product, filterKey);
    if (filterKey !== 'brand') return tagValues;

    const values = [...tagValues];
    const addBrand = (brand) => {
      if (!brand) return;
      const lookup = brand.toLowerCase();
      if (!values.some((value) => value.toLowerCase() === lookup)) values.push(brand);
    };

    addBrand(this.getBrandFromTitle(product));

    const vendor = String(product?.vendor || '').trim();
    if (vendor) {
      const allowed = this.getAllowedTagsForFilter('brand');
      const vendorMatch = allowed.find((value) => value.toLowerCase() === vendor.toLowerCase());
      addBrand(vendorMatch);
    }

    return values;
  }

  getAvailableBrandOptions() {
    const fromFiltered = this.collectTagOptionsFromEntries(this.getFilteredPool('brand'), 'brand');
    if (fromFiltered.length) return fromFiltered;

    const fromPool = this.collectTagOptionsFromEntries(this.allEntries, 'brand');
    return fromPool;
  }

  collectTagOptionsFromEntries(entries, filterKey) {
    const allowed = this.getAllowedTagsForFilter(filterKey);
    if (!allowed.length) return [];

    const allowedLookup = new Set(allowed.map((tag) => tag.toLowerCase()));
    const found = new Map();

    entries.forEach((entry) => {
      this.getEntryValuesForFilter(entry.product, filterKey).forEach((value) => {
        const lookup = value.toLowerCase();
        if (!allowedLookup.has(lookup)) return;
        const canonical = allowed.find((item) => item.toLowerCase() === lookup) || value;
        found.set(canonical.toLowerCase(), canonical);
      });
    });

    return [...found.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
    );
  }

  getAvailableTagGroupOptions(filterKey) {
    const allowed = this.getAllowedTagsForFilter(filterKey);
    if (!allowed.length) return [];

    const fromFiltered = this.collectTagOptionsFromEntries(this.getFilteredPool(filterKey), filterKey);
    if (fromFiltered.length) return fromFiltered;

    const fromPool = this.collectTagOptionsFromEntries(this.allEntries, filterKey);
    if (fromPool.length) return fromPool;

    return allowed;
  }

  getAvailableColorOptions() {
    return this.getAvailableTagGroupOptions('color');
  }

  getAvailableFeaturesOptions() {
    return this.getAvailableTagGroupOptions('features');
  }

  getAvailableOptionsForFilter(filterKey) {
    return this.collectTagOptionsFromEntries(this.getFilteredPool(filterKey), filterKey);
  }

  getAvailableGenderOptions() {
    const found = new Set();
    this.getFilteredPool('gender').forEach((entry) => {
      this.getEntryGenders(entry.product).forEach((gender) => found.add(gender));
    });
    const available = TnwsFavoritesPage.GENDER_OPTIONS.filter((gender) => found.has(gender));
    return available.length ? available : TnwsFavoritesPage.GENDER_OPTIONS;
  }

  isNewStockTag(tag) {
    return String(tag).trim().toLowerCase() === 'new stock';
  }

  isRefurbishedTag(tag) {
    const lower = String(tag).trim().toLowerCase();
    return TnwsFavoritesPage.REFURBISHED_TAGS.has(lower) || lower.includes('refurbish');
  }

  getEntryQualities(product) {
    const qualities = new Set();
    this.normalizeTags(product).forEach((tag) => {
      if (this.isNewStockTag(tag)) qualities.add('New stock');
      if (this.isRefurbishedTag(tag)) qualities.add('Refurbished');
    });
    return [...qualities];
  }

  getAvailableQualityOptions() {
    const found = new Set();
    this.getFilteredPool('quality').forEach((entry) => {
      this.getEntryQualities(entry.product).forEach((quality) => found.add(quality));
    });
    const available = TnwsFavoritesPage.QUALITY_OPTIONS.filter((quality) => found.has(quality));
    return available.length ? available : TnwsFavoritesPage.QUALITY_OPTIONS;
  }

  matchesQualityFilter(product, selectValue) {
    const values = this.getEntryQualities(product);
    const select = (selectValue || '').trim();
    if (select) return values.some((value) => String(value).toLowerCase() === select.toLowerCase());
    return true;
  }

  getEntryGenders(product) {
    const genders = new Set();
    const tags = this.normalizeTags(product).map((tag) => tag.toLowerCase());
    const title = String(product.title || '').toLowerCase();
    const blob = `${title} ${tags.join(' ')}`;

    if (/\b(men|mens|men's|male|for him)\b/.test(blob)) genders.add("Men's");
    if (/\b(women|womens|women's|ladies|female|for her)\b/.test(blob)) genders.add("Women's");
    if (/\bunisex\b/.test(blob)) genders.add('Unisex');

    return [...genders];
  }

  matchesGenderFilter(product, selectValue) {
    const values = this.getEntryGenders(product);
    const select = (selectValue || '').trim();
    if (select) {
      return values.some((value) => String(value).toLowerCase() === select.toLowerCase());
    }
    return true;
  }

  matchesGroupFilter(product, filterKey, selectValue) {
    const values = this.getEntryValuesForFilter(product, filterKey);
    const select = (selectValue || '').trim();
    if (select) {
      return values.some((value) => String(value).toLowerCase() === select.toLowerCase());
    }
    return true;
  }

  getEntryPriceCents(entry) {
    return entry.variant?.price ?? entry.product.price ?? 0;
  }

  getPriceBounds(filters = this.getFilterState()) {
    if (filters.priceMin !== '' || filters.priceMax !== '') {
      const min =
        filters.priceMin !== '' && !Number.isNaN(Number(filters.priceMin))
          ? Math.round(Number(filters.priceMin) * 100)
          : null;
      const max =
        filters.priceMax !== '' && !Number.isNaN(Number(filters.priceMax))
          ? Math.round(Number(filters.priceMax) * 100)
          : null;
      return { min, max };
    }

    const preset = this.pricePresets[filters.pricePreset];
    if (preset) return { min: preset.min, max: preset.max };
    return { min: null, max: null };
  }

  normalizeTags(product) {
    const raw = product?.tags;
    if (Array.isArray(raw)) {
      return raw.map((tag) => String(tag).trim()).filter(Boolean);
    }
    if (typeof raw === 'string') {
      return raw
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  }

  matchesFilters(entry, filters = this.getFilterState()) {
    const { product } = entry;
    const title = String(product.title || '').toLowerCase();

    if (filters.title && !title.includes(filters.title)) return false;

    if (filters.brandSelect) {
      if (!this.matchesGroupFilter(product, 'brand', filters.brandSelect)) return false;
    }

    if (filters.caseSelect) {
      if (!this.matchesGroupFilter(product, 'case', filters.caseSelect)) return false;
    }

    if (filters.colorSelect) {
      if (!this.matchesGroupFilter(product, 'color', filters.colorSelect)) return false;
    }

    if (filters.genderSelect) {
      if (!this.matchesGenderFilter(product, filters.genderSelect)) return false;
    }

    if (filters.featuresSelect) {
      if (!this.matchesGroupFilter(product, 'features', filters.featuresSelect)) return false;
    }

    if (filters.qualitySelect) {
      if (!this.matchesQualityFilter(product, filters.qualitySelect)) return false;
    }

    const { min, max } = this.getPriceBounds(filters);
    if (min != null || max != null) {
      const price = this.getEntryPriceCents(entry);
      if (min != null && price < min) return false;
      if (max != null && price > max) return false;
    }

    return true;
  }

  setLoading(isLoading) {
    if (this.skeleton) this.skeleton.hidden = !isLoading;
  }

  setFiltersVisible(visible) {
    if (!visible) {
      this.closePillDropdowns();
      this.closeMoreDrawer();
    }
    if (this.filters) this.filters.hidden = !visible;
  }

  updateCountLabel(totalCount) {
    if (!this.countLabel) return;

    const strings = window.tnwsFavoritesStrings || {};

    if (totalCount <= 0) {
      this.countLabel.hidden = true;
      this.countLabel.textContent = '';
      return;
    }

    const template = totalCount === 1 ? strings.countOne : strings.countOther;
    this.countLabel.textContent = template
      ? template.replace('__COUNT__', String(totalCount))
      : `${totalCount} saved watch${totalCount === 1 ? '' : 'es'}`;
    this.countLabel.hidden = false;
  }

  updateFilterStatus(filteredCount, totalCount) {
    if (!this.filterStatus) return;

    const active = this.hasActiveFilters();
    if (!active || totalCount <= 0) {
      this.filterStatus.hidden = true;
      this.filterStatus.textContent = '';
      return;
    }

    const strings = window.tnwsFavoritesStrings || {};
    this.filterStatus.textContent = strings.filterShowing
      ? strings.filterShowing
          .replace('__COUNT__', String(filteredCount))
          .replace('__TOTAL__', String(totalCount))
      : `Showing ${filteredCount} of ${totalCount} saved watches`;
    this.filterStatus.hidden = false;
  }

  applyFilters() {
    if (!this.grid || !this.empty) return;

    const total = this.allEntries.length;
    const filtered = this.allEntries.filter((entry) => this.matchesFilters(entry));
    const active = this.hasActiveFilters();

    this.lastVisibleCount = filtered.length;
    this.updateCountLabel(total);
    this.updateFilterStatus(filtered.length, total);
    this.updateMobileFilterUi(filtered.length);

    if (this.filteredEmpty) this.filteredEmpty.hidden = true;
    this.empty.hidden = true;
    this.grid.hidden = true;
    this.grid.innerHTML = '';

    if (total === 0) {
      this.setFiltersVisible(false);
      this.empty.hidden = false;
      return;
    }

    this.setFiltersVisible(true);
    this.populateFilterOptions();

    if (filtered.length === 0) {
      if (active && this.filteredEmpty) {
        this.filteredEmpty.hidden = false;
      } else {
        this.empty.hidden = false;
      }
      return;
    }

    this.grid.hidden = false;
    this.grid.innerHTML = filtered.map((entry) => this.renderCard(entry)).join('');
    TnwsFavorites.syncButtons();
  }

  async render() {
    if (!this.grid || !this.empty) return;

    this.setLoading(true);
    this.grid.hidden = true;
    this.empty.hidden = true;
    if (this.filteredEmpty) this.filteredEmpty.hidden = true;
    this.setFiltersVisible(false);

    let items = [];
    try {
      items = TnwsFavoritesConfig.isLoggedIn()
        ? await TnwsFavorites.fetchRemoteItems()
        : TnwsFavorites.getItems();
    } catch (e) {
      items = TnwsFavorites.getItems();
    }

    if (items.length === 0) {
      this.allEntries = [];
      this.grid.innerHTML = '';
      this.setLoading(false);
      this.updateCountLabel(0);
      this.updateFilterStatus(0, 0);
      this.empty.hidden = false;
      return;
    }

    const cards = await Promise.all(
      items.map(async (item) => {
        try {
          const response = await fetch(`/products/${item.handle}.js`);
          if (!response.ok) return null;
          const product = await response.json();

          if (item.variant_id) {
            const variant = product.variants?.find((v) => String(v.id) === String(item.variant_id));
            if (!variant) return null;
            return { product, variant, item };
          }

          return { product, variant: null, item };
        } catch (e) {
          return null;
        }
      })
    );

    this.allEntries = cards.filter(Boolean);
    this.setLoading(false);
    this.populateFilterOptions();

    if (this.allEntries.length === 0) {
      this.grid.innerHTML = '';
      this.empty.hidden = false;
      this.updateCountLabel(0);
      this.updateFilterStatus(0, 0);
      return;
    }

    this.applyFilters();
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  sizedImage(url, width) {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  renderPrice(priceCents, compareCents) {
    const price = this.formatMoney(priceCents);
    const onSale = compareCents && compareCents > priceCents;
    const compare = onSale ? this.formatMoney(compareCents) : '';

    return `
      <div class="price${onSale ? ' price--on-sale' : ''}">
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular">${price}</span>
          </div>
          <div class="price__sale">
            <span><s class="price-item price-item--regular">${compare}</s></span>
            <span class="price-item price-item--sale price-item--last">${price}</span>
          </div>
        </div>
      </div>
    `;
  }

  renderQuickAdd(product, variant) {
    if (!product.available || (product.variants?.length || 0) !== 1) return '';
    const variantId = variant?.id || product.variants[0].id;
    if (variant && !variant.available) return '';

    const label = window.variantStrings?.addToCart || 'Add to cart';
    const cartAddUrl = window.routes?.cart_add_url || '/cart/add';

    return `
      <product-form class="tnws-quick-add" data-hide-errors="true">
        <form method="post" action="${this.escapeAttr(cartAddUrl)}" class="tnws-quick-add__form" novalidate>
          <input type="hidden" name="id" value="${this.escapeAttr(variantId)}">
          <input type="hidden" name="quantity" value="1">
          <button
            type="submit"
            name="add"
            class="tnws-quick-add__btn"
            aria-label="${this.escapeAttr(`${label}: ${product.title}`)}"
            title="${this.escapeAttr(label)}"
          >
            <span class="visually-hidden">${this.escape(label)}</span>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <path stroke="currentColor" stroke-width="1.6" stroke-linecap="round" d="M10 4.5v11M4.5 10h11"/>
            </svg>
          </button>
        </form>
      </product-form>
    `;
  }

  renderConditionBadge(product) {
    const tags = this.normalizeTags(product);
    let condition = '';
    if (tags.some((tag) => this.isRefurbishedTag(tag))) condition = 'refurbished';
    else if (tags.some((tag) => this.isNewStockTag(tag))) condition = 'new';
    if (!condition) return '';

    const strings = window.tnwsFavoritesStrings || {};
    const isRefurbished = condition === 'refurbished';
    const letter = isRefurbished ? 'R' : 'N';
    const title = isRefurbished
      ? strings.conditionRefurbishedTitle || 'Refurbished'
      : strings.conditionNewTitle || 'New stock';
    const text = isRefurbished
      ? strings.conditionRefurbishedText || 'Pre-owned, inspected and restored by us — ready to wear.'
      : strings.conditionNewText || 'Brand-new, unworn watch.';

    return `
      <span
        class="tnws-condition-badge tnws-condition-badge--${condition}"
        tabindex="0"
        aria-label="${this.escapeAttr(`${title}: ${text}`)}"
      >
        <span class="tnws-condition-badge__letter" aria-hidden="true">${letter}</span>
        <span class="tnws-condition-badge__tip">
          <strong>${this.escape(title)}</strong>
          ${this.escape(text)}
          <a class="tnws-condition-badge__link" href="${this.escapeAttr(strings.conditionGradesUrl || '/pages/condition-grades')}">${this.escape(strings.conditionLearnMore || 'What the grades mean')}<span aria-hidden="true">&rarr;</span></a>
        </span>
      </span>
    `;
  }

  renderCard({ product, variant, item }) {
    const colorScheme = this.root.dataset.cardColorScheme || 'scheme-1';
    const primaryImage = variant?.featured_image?.src || product.featured_image || product.images?.[0];
    const secondaryImage = product.images?.[1];
    const imageSizes =
      '(min-width: 990px) calc((100vw - 10rem) / 3), (min-width: 750px) calc((100vw - 10rem) / 2), calc((100vw - 3rem) / 2)';
    const imageHtml = primaryImage
      ? `<img
          src="${this.sizedImage(primaryImage, 1066)}"
          srcset="${this.sizedImage(primaryImage, 360)} 360w, ${this.sizedImage(primaryImage, 533)} 533w, ${this.sizedImage(primaryImage, 720)} 720w, ${this.sizedImage(primaryImage, 1066)} 1066w"
          sizes="${imageSizes}"
          alt="${this.escape(product.title)}"
          loading="lazy"
          width="1066"
          height="1066"
          class="motion-reduce"
        >`
      : '';
    const secondaryHtml =
      secondaryImage && secondaryImage !== primaryImage
        ? `<img
            src="${this.sizedImage(secondaryImage, 1066)}"
            srcset="${this.sizedImage(secondaryImage, 360)} 360w, ${this.sizedImage(secondaryImage, 533)} 533w, ${this.sizedImage(secondaryImage, 720)} 720w, ${this.sizedImage(secondaryImage, 1066)} 1066w"
            sizes="${imageSizes}"
            alt=""
            loading="lazy"
            width="1066"
            height="1066"
            class="motion-reduce"
            aria-hidden="true"
          >`
        : '';

    const priceCents = variant?.price ?? product.price;
    const compareCents = variant?.compare_at_price ?? product.compare_at_price;
    const productUrl = variant ? `${product.url}?variant=${variant.id}` : product.url;

    return `
      <li class="grid__item">
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <button
            type="button"
            class="tnws-favorite-btn tnws-favorite-btn--card is-favorited"
            data-tnws-favorite
            data-product-id="${product.id}"
            data-product-handle="${product.handle}"
            data-variant-id="${variant?.id || ''}"
            data-sku="${this.escapeAttr(item.sku || variant?.sku || '')}"
            aria-pressed="true"
            aria-label="${this.escapeAttr(window.tnwsFavoritesStrings?.remove || 'Remove from favorites')}"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-tudor-heart" viewBox="0 0 20 20" aria-hidden="true">
              <path stroke="currentColor" stroke-width="1.35" stroke-linejoin="round" d="M10 15.1S4.7 11.7 4.7 7.8C4.7 5.45 6.55 3.7 8.6 3.7c1.15 0 2.1.58 2.65 1.42.55-.84 1.5-1.42 2.65-1.42 2.05 0 3.9 1.75 3.9 4.1 0 3.9-5.85 7.3-5.85 7.3H10z"/>
            </svg>
          </button>
          ${this.renderConditionBadge(product)}
          <div class="card card--standard card--media" style="--ratio-percent: 100%;">
            <div class="card__inner color-${this.escapeAttr(colorScheme)} gradient ratio" style="--ratio-percent: 100%;">
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  ${imageHtml}
                  ${secondaryHtml}
                </div>
              </div>
              ${this.renderQuickAdd(product, variant)}
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${productUrl}" class="full-unstyled-link" title="${this.escapeAttr(product.title)}">
                    ${this.escape(product.title)}
                  </a>
                </h3>
                <div class="card-information">
                  ${this.renderPrice(priceCents, compareCents)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text ?? '';
    return div.innerHTML;
  }

  escapeAttr(text) {
    return String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new TnwsFavoriteButtons();
  const page = document.querySelector('[data-tnws-favorites-page]');
  if (page) new TnwsFavoritesPage(page);
});
